import {
  AkVertrag, OPT_AK_ART, OPT_AV_ART, OPT_SACH, OPT_SACH_NONE, SachVertrag, VorsorgeVertrag,
} from "../state/schema";
import {
  card, h, jaNein, note, objSelect, objText, textareaInput, unknownToggle,
} from "../components/ui";
import { uploadBlock } from "../components/upload";
import { vAltersvorsorge, vArbeitskraft, vGesundheit, vSach } from "../logic/validation";
import { Step } from "./types";

const newId = () => "v-" + Math.random().toString(36).slice(2, 10);
const persist = (ctx: { store: { persist(): void } }) => () => ctx.store.persist();

const POLIZA_HINT = "La póliza es el documento de tu contrato. Si no la tienes a mano, bastan también las primeras una o dos páginas de la solicitud.";

/* ---------- 5 · Salud y factores de riesgo ---------- */
export const stepGesundheit: Step = {
  id: "gesundheit",
  section: "absicherung",
  eyebrow: "Protección",
  title: "Salud y factores de riesgo",
  subtitle: "Solo sí/no: por favor, sin diagnósticos ni detalles.",
  validate: vGesundheit,
  build(ctx) {
    const s = ctx.store;
    const hobbyDetail = h("div");
    const renderHobby = () => {
      hobbyDetail.innerHTML = "";
      if (s.get("gesundheit.risikohobby") === "ja") {
        hobbyDetail.appendChild(h("div", { style: { marginTop: "10px" } },
          textareaInput(s, "gesundheit.risikohobbyText", { label: "¿Qué pasatiempos practicas?", required: true, rows: 2, placeholder: "p. ej. moto, escalada, buceo …" })));
      }
    };
    renderHobby();
    return h(
      "div",
      {},
      card(
        h("div", { class: "card__title", html: "Salud física <span style='color:var(--neg)'>*</span>" }),
        h("p", { class: "card__hint", text: "¿Has tenido o tienes en los últimos cinco años enfermedades físicas, molestias, lesiones, operaciones o tratamientos?" }),
        jaNein(s, "gesundheit.koerperlich")
      ),
      card(
        h("div", { class: "card__title", html: "Salud mental <span style='color:var(--neg)'>*</span>" }),
        h("p", { class: "card__hint", text: "¿Has tenido o tienes en los últimos cinco años molestias, enfermedades, tratamientos o consultas psíquicas o psicosomáticas?" }),
        jaNein(s, "gesundheit.psychisch"),
        note("info", "Por favor, no indiques aquí diagnósticos ni detalles. Si hace falta una revisión más precisa, lo hablaremos después contigo por separado y de forma confidencial.")
      ),
      card(
        h("div", { class: "card__title", html: "¿Fumas? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "gesundheit.raucher")
      ),
      card(
        h("div", { class: "card__title", html: "¿Practicas pasatiempos de riesgo o poco habituales? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "gesundheit.risikohobby", renderHobby),
        hobbyDetail
      )
    );
  },
};

/* ---------- 6 · Previsión de jubilación ---------- */
export const stepAltersvorsorge: Step = {
  id: "altersvorsorge",
  section: "absicherung",
  eyebrow: "Protección",
  title: "Previsión de jubilación",
  subtitle: "¿Ya has previsto algo para el futuro?",
  validate: vAltersvorsorge,
  build(ctx) {
    const s = ctx.store;
    const list = h("div");
    const p = persist(ctx);

    const renderCard = (v: VorsorgeVertrag, idx: number): HTMLElement => {
      const body = h("div");
      const draw = () => {
        body.innerHTML = "";
        body.append(
          objSelect(v, "vertragsart", OPT_AV_ART, p, { label: "Tipo de contrato", required: true }),
          objText(v, "anbieter", p, { label: "Compañía", required: true, disabled: v.anbieterUnbekannt }),
          unknownToggle(v, "anbieterUnbekannt", p, draw, "No conozco la compañía"),
          objText(v, "beitrag", p, { label: "Cuota mensual", money: true, required: true, disabled: v.beitragUnbekannt }),
          unknownToggle(v, "beitragUnbekannt", p, draw, "No conozco la cuota"),
          h("div", { class: "grid-2" },
            objText(v, "produkt", p, { label: "Nombre del producto", optional: true }),
            objText(v, "vertragsnummer", p, { label: "Número de contrato", optional: true })),
          h("div", { class: "grid-2" },
            objText(v, "beginn", p, { label: "Inicio del contrato", optional: true, placeholder: "p. ej. 2019" }),
            objText(v, "ende", p, { label: "Fin del contrato", optional: true })),
          objText(v, "hochrechnung", p, { label: "Proyección", optional: true }),
          h("div", { class: "field" },
            h("div", { class: "field__label", text: "Póliza de seguro (opcional)" }),
            h("p", { class: "field__optional", text: POLIZA_HINT }),
            uploadBlock(s, { kategorie: "Altersvorsorge" }))
        );
      };
      draw();
      return h("div", { class: "subcard" },
        h("div", { class: "subcard__head" },
          h("div", { class: "subcard__title", text: `Contrato ${idx + 1}` }),
          h("button", { class: "btn-remove", type: "button", text: "Quitar",
            on: { click: () => { s.update((st) => { st.altersvorsorge.vertraege.splice(idx, 1); }, false); renderList(); } } })),
        body);
    };

    const renderList = () => {
      list.innerHTML = "";
      if (s.get("altersvorsorge.vorhanden") !== "ja") return;
      s.state.altersvorsorge.vertraege.forEach((v, i) => list.appendChild(renderCard(v, i)));
      list.appendChild(h("button", { class: "btn-add", type: "button", text: "＋ Añadir otra previsión de jubilación",
        on: { click: () => { s.update((st) => st.altersvorsorge.vertraege.push(emptyVorsorge()), false); renderList(); } } }));
    };

    const onToggle = () => {
      if (s.get("altersvorsorge.vorhanden") === "ja" && s.state.altersvorsorge.vertraege.length === 0)
        s.update((st) => st.altersvorsorge.vertraege.push(emptyVorsorge()), false);
      renderList();
    };
    renderList();

    return h("div", {},
      card(
        h("div", { class: "card__title", html: "¿Ya tienes un seguro de pensión privado o en tu trabajo? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "altersvorsorge.vorhanden", onToggle, true)),
      list);
  },
};

function emptyVorsorge(): VorsorgeVertrag {
  return { id: newId(), vertragsart: "", anbieter: "", anbieterUnbekannt: false, beitrag: "", beitragUnbekannt: false, produkt: "", beginn: "", ende: "", hochrechnung: "", vertragsnummer: "" };
}

/* ---------- 7 · Protección de la capacidad laboral ---------- */
export const stepArbeitskraft: Step = {
  id: "arbeitskraft",
  section: "absicherung",
  eyebrow: "Protección",
  title: "Protección de la capacidad laboral",
  subtitle: "¿Qué pasa si ya no puedes trabajar?",
  validate: vArbeitskraft,
  build(ctx) {
    const s = ctx.store;
    const list = h("div");
    const p = persist(ctx);

    const renderCard = (v: AkVertrag, idx: number): HTMLElement => {
      const body = h("div");
      const draw = () => {
        body.innerHTML = "";
        body.append(
          objSelect(v, "vertragsart", OPT_AK_ART, p, { label: "Tipo de cobertura", required: true }),
          objText(v, "anbieter", p, { label: "Compañía", required: true, disabled: v.anbieterUnbekannt }),
          unknownToggle(v, "anbieterUnbekannt", p, draw, "No conozco la compañía"),
          objText(v, "beitrag", p, { label: "Cuota mensual", money: true, disabled: v.beitragUnbekannt }),
          unknownToggle(v, "beitragUnbekannt", p, draw, "No conozco la cuota"),
          objText(v, "leistung", p, { label: "Prestación asegurada", placeholder: "p. ej. renta mensual", disabled: v.leistungUnbekannt }),
          unknownToggle(v, "leistungUnbekannt", p, draw, "No conozco la prestación"),
          h("div", { class: "grid-2" },
            objText(v, "beginn", p, { label: "Inicio del contrato", optional: true }),
            objText(v, "ende", p, { label: "Fin del contrato", optional: true })),
          objText(v, "vertragsnummer", p, { label: "Número de contrato", optional: true }),
          h("div", { class: "field" },
            h("div", { class: "field__label", text: "Póliza de seguro (opcional)" }),
            h("p", { class: "field__optional", text: POLIZA_HINT }),
            uploadBlock(s, { kategorie: "Arbeitskraftabsicherung" }))
        );
      };
      draw();
      return h("div", { class: "subcard" },
        h("div", { class: "subcard__head" },
          h("div", { class: "subcard__title", text: `Cobertura ${idx + 1}` }),
          h("button", { class: "btn-remove", type: "button", text: "Quitar",
            on: { click: () => { s.update((st) => { st.arbeitskraft.vertraege.splice(idx, 1); }, false); renderList(); } } })),
        body);
    };

    const renderList = () => {
      list.innerHTML = "";
      if (s.get("arbeitskraft.vorhanden") !== "ja") return;
      s.state.arbeitskraft.vertraege.forEach((v, i) => list.appendChild(renderCard(v, i)));
      list.appendChild(h("button", { class: "btn-add", type: "button", text: "＋ Añadir otra cobertura",
        on: { click: () => { s.update((st) => st.arbeitskraft.vertraege.push(emptyAk()), false); renderList(); } } }));
    };
    const onToggle = () => {
      if (s.get("arbeitskraft.vorhanden") === "ja" && s.state.arbeitskraft.vertraege.length === 0)
        s.update((st) => st.arbeitskraft.vertraege.push(emptyAk()), false);
      renderList();
    };
    renderList();

    return h("div", {},
      card(
        h("div", { class: "card__title", html: "¿Ya tienes una cobertura para tu capacidad laboral? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "arbeitskraft.vorhanden", onToggle, true)),
      list);
  },
};

function emptyAk(): AkVertrag {
  return { id: newId(), vertragsart: "", anbieter: "", anbieterUnbekannt: false, beitrag: "", beitragUnbekannt: false, leistung: "", leistungUnbekannt: false, beginn: "", ende: "", vertragsnummer: "" };
}

/* ---------- 8 · Seguros del día a día y de bienes ---------- */
export const stepSach: Step = {
  id: "sachversicherungen",
  section: "absicherung",
  eyebrow: "Protección",
  title: "Seguros del día a día y de bienes",
  subtitle: "¿Qué seguros tienes actualmente? Puedes elegir varios.",
  leaveMessage: "Tu protección está registrada.",
  validate: vSach,
  build(ctx) {
    const s = ctx.store;
    const p = persist(ctx);
    const cardsWrap = h("div");

    const ensureVertraege = () => {
      const auswahl: string[] = s.get("sachversicherungen.auswahl");
      const echte = auswahl.filter((a) => !OPT_SACH_NONE.includes(a));
      s.update((st) => {
        // eliminar tarjetas sin selección
        st.sachversicherungen.vertraege = st.sachversicherungen.vertraege.filter((v) => echte.includes(v.art));
        // añadir las que falten
        for (const art of echte) if (!st.sachversicherungen.vertraege.some((v) => v.art === art))
          st.sachversicherungen.vertraege.push(emptySach(art));
      }, false);
    };

    const toggle = (opt: string) => {
      s.update((st) => {
        const arr = st.sachversicherungen.auswahl;
        const i = arr.indexOf(opt);
        if (i >= 0) arr.splice(i, 1);
        else {
          // "Ninguno"/"No lo sé" excluyen la selección real y viceversa
          if (OPT_SACH_NONE.includes(opt)) st.sachversicherungen.auswahl = [opt];
          else {
            for (const none of OPT_SACH_NONE) {
              const ni = arr.indexOf(none);
              if (ni >= 0) arr.splice(ni, 1);
            }
            arr.push(opt);
          }
        }
      }, false);
      ensureVertraege();
      renderChips();
      renderCards();
    };

    const chips = h("div", { class: "chips" });
    const renderChips = () => {
      chips.innerHTML = "";
      const auswahl: string[] = s.get("sachversicherungen.auswahl");
      [...OPT_SACH, ...OPT_SACH_NONE].forEach((opt) => {
        const active = auswahl.includes(opt);
        chips.appendChild(h("button", { type: "button", class: "chip", text: opt, attrs: { "aria-pressed": String(active) }, on: { click: () => toggle(opt) } }));
      });
    };

    const renderCards = () => {
      cardsWrap.innerHTML = "";
      s.state.sachversicherungen.vertraege.forEach((v) => cardsWrap.appendChild(renderSachCard(v, s, p)));
    };

    renderChips();
    renderCards();

    return h("div", {},
      card(
        h("div", { class: "card__title", html: "¿Qué seguros tienes actualmente? <span style='color:var(--neg)'>*</span>" }),
        chips),
      cardsWrap);
  },
};

function emptySach(art: string): SachVertrag {
  return { id: newId(), art, anbieter: "", anbieterUnbekannt: false, beitrag: "", intervall: "mensual", beginn: "", ende: "", leistung: "", vertragsnummer: "" };
}

function renderSachCard(v: SachVertrag, s: any, p: () => void): HTMLElement {
  const body = h("div");
  const draw = () => {
    body.innerHTML = "";
    body.append(
      objText(v, "anbieter", p, { label: "Compañía", disabled: v.anbieterUnbekannt }),
      unknownToggle(v, "anbieterUnbekannt", p, draw, "No conozco la compañía"),
      h("div", { class: "grid-2" },
        objText(v, "beitrag", p, { label: "Cuota", money: true }),
        objSelect(v, "intervall" as any, ["mensual", "anual"], p, { label: "Frecuencia de pago" })),
      h("div", { class: "grid-2" },
        objText(v, "beginn", p, { label: "Inicio del contrato", optional: true }),
        objText(v, "ende", p, { label: "Fin del contrato / plazo de cancelación", optional: true })),
      objText(v, "leistung", p, { label: "Prestación", optional: true }),
      objText(v, "vertragsnummer", p, { label: "Número de contrato", optional: true }),
      h("div", { class: "field" },
        h("div", { class: "field__label", text: "Póliza de seguro (opcional)" }),
        h("p", { class: "field__optional", text: POLIZA_HINT }),
        uploadBlock(s, { kategorie: "Sachversicherung" }))
    );
  };
  draw();
  return h("div", { class: "subcard" }, h("div", { class: "subcard__title", style: { marginBottom: "10px" }, text: v.art }), body);
}
